import React, { useMemo, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import MediaViewerModal from '../MediaViewer/MediaViewerModal';
import AttachmentButton from '../Button/AttachmentButton';
import useTranslation from '../../Hooks/useTranslation';
import InsuranceReportDetailModal from '../../Modules/Insurance/Component/InsuranceReportDetail/InsuranceReportDetailModal';
import { PreviewFile } from '../MediaViewer/MediaViewer';

interface DekurzAttachmentProps {
	item: {
		id: string;
		type: string;
		extra: {
			id: string;
			title?: string;
			sentToPatient?: boolean;
			folderId?: string;
		};
	};
	key: string;
	onRemoveAttachment: (itemId: string) => void;
	patientId: string;
	virtualPatientId: string;
}

export default function DekurzAttachment(props: DekurzAttachmentProps) {
	const { item, onRemoveAttachment, patientId, virtualPatientId } = props;

	const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
	const [isInsuranceReportOpened, setIsInsuranceReportOpened] = useState(false);
	const [isDeleted, setIsDeleted] = useState(false);
	const { socketUrl, links, isLatam } = useAppContext();
	const { t } = useTranslation();

	const itemName = useMemo(() => {
		let name = '';

		if (item?.extra?.title) {
			return item?.extra?.title;
		}

		switch (item.type) {
			case 'medicalReportPatient':
				name = 'base.shared.medicalReport';
				break;
			case 'medicalReport':
				name = 'base.shared.medicalExamination';
				break;
			case 'prescription':
				name = 'base.ePrescription';
				break;
			case 'appointment':
				name = 'base.appointment';
				break;
			case 'insuranceReport':
				name = 'base.insuranceReport';
				break;
		}
		return t(name);
	}, [item]);

	const additionalText = item?.extra?.sentToPatient
		? t('component.medicalReport.sentToPatient')
		: undefined;

	const handleRemove = () => {
		setIsDeleted(true);
		onRemoveAttachment(item.id);
	};

	const fileToViewer: PreviewFile = {
		id: item.extra.id,
		displayName: itemName ?? '',
		type: 'pdf',
		preview: socketUrl + '/document/' + item.extra.id + '/pdf/download',
		download: socketUrl + '/document/' + item.extra.id + '/pdf/download',
	};
	if (item.type === 'prescription') {
		let prescriptionLink = links.treatment.downloadPrescription
			.replace('__PATIENT__', patientId)
			.replace('__ID__', item.extra.id);

		// TODO: CZECH TYPE
		if (!isLatam) {
			prescriptionLink = links.treatment.downloadPrescriptionSukl
				.replace('__PATIENT__', patientId)
				.replace('__ID__', item.extra.id);
		}

		fileToViewer.preview = prescriptionLink;
		fileToViewer.download = prescriptionLink;
	}

	// IF TYPE IS documentationFile NEED TO HAVE OTHER URL TO FILE
	if (item.type === 'documentationFile') {
		const documentationFileLink = `${socketUrl}/patients/${virtualPatientId}/documentation/${item.extra.folderId}/${item.extra.id}`;
		fileToViewer.preview = documentationFileLink;
		fileToViewer.download = documentationFileLink;
	}

	const handleOpenAttachment = () => {
		if (item.type === 'insuranceReport') {
			setIsInsuranceReportOpened(true);
		} else {
			setMediaViewerOpened(true);
		}
	};

	if (isDeleted || !item?.extra?.id) {
		return null;
	}

	return (
		<div className={'mb-3'}>
			<AttachmentButton
				onClick={handleOpenAttachment}
				title={itemName}
				onRemove={handleRemove}
				additionalText={additionalText}
			/>
			<MediaViewerModal
				opened={mediaViewerOpened}
				onClose={() => setMediaViewerOpened(false)}
				file={fileToViewer}
			/>
			{item.type === 'insuranceReport' &&
				item.extra.id &&
				isInsuranceReportOpened && (
					<InsuranceReportDetailModal
						insuranceReportId={item.extra.id}
						isOpen
						onClose={() => setIsInsuranceReportOpened(false)}
					/>
				)}
		</div>
	);
}
